<template>
  <div
    :class="[
      'sidebar-management-custom',
      props.className,
      { 'is-collapsed': isCollapsed },
    ]"
    :style="[props.inlineStyles]"
  >
    <div class="sidebar-section">
      <div
        v-if="props.isShowLogo"
        class="is-logo"
        @click="
          () => {
            onClickLogo();
          }
        "
      >
        <img :src="props.logoImage" alt="Logo" />
      </div>

      <div class="flex items-center gap-4 mb-8" v-if="!isCollapsed">
        <img
          v-if="props.userImage"
          className="rounded-full border border-white box-sizing min-w-[3.5rem] max-w-[3.5rem] min-h-[3.5rem] max-h-[3.5rem]"
          :src="props.userImage"
        />
        <span
          v-if="props.userName"
          class="text-white font-semibold text-xl text-ellipsis overflow-hidden max-w-36"
          :title="props.userName"
        >
          {{ props.userName }}
        </span>
      </div>

      <!-- Collapsed User Avatar -->
      <div
        class="flex justify-center mb-8"
        v-if="isCollapsed && props.userImage"
      >
        <img
          className="rounded-full border border-white box-sizing min-w-[2.5rem] max-w-[2.5rem] min-h-[2.5rem] max-h-[2.5rem]"
          :src="props.userImage"
          :title="props.userName"
        />
      </div>

      <div class="sidebar-lists flex flex-col gap-4">
        <div
          v-for="(menu, index) in props.menuList"
          class="flex space-x-2 justify-between items-center space-y-4"
          :key="index"
        >
          <div
            :class="[
              'button-box space-y-4 flex-1',
              {
                'is-selected':
                  menu.activeNameList.indexOf(
                    $router.currentRoute.value.name
                  ) >= 0,
                'is-active': menu.isShowSubMenu,
                'is-collapsed': isCollapsed,
              },
            ]"
            @click="
              () => {
                if (
                  !menu?.subMenu &&
                  $router.currentRoute.value.fullPath != menu.path &&
                  menu.path
                ) {
                  props.onClickSubMenu(index, false);
                  $router.push(menu.path);
                } else {
                  props.onClickSubMenu && props.onClickSubMenu(index);
                }
              }
            "
            :title="isCollapsed ? menu.name : ''"
          >
            <!-- ...existing code for menu items... -->
            <div
              v-if="!menu?.subMenu"
              :class="[
                'sidebar-button items-start flex text-white mb-4 mx-auto p-2 cursor-pointer',
                {
                  'rounded-[10px] text-[#64748b]': menu.isActive,
                  'justify-center': isCollapsed,
                },
              ]"
            >
              <div class="sidebar-image flex-none">
                <img
                  width="24"
                  height="24"
                  :src="menu.isActive ? menu.iconActive : menu.icon"
                  alt="Icon"
                />
              </div>
              <div
                v-if="!isCollapsed"
                :class="[
                  'sidebar-text flex-1 line-clamp-1',
                  { 'text-[#64748b]': menu.isActive },
                ]"
                :title="menu.name"
              >
                {{ menu.name }}
              </div>
            </div>
            <div v-else class="flex flex-col">
              <div
                :class="[
                  'sidebar-button items-start flex text-white mb-4 mx-auto p-2 cursor-pointer',
                  {
                    'rounded-[10px] text-[#64748b]': menu.isActive,
                    'justify-center': isCollapsed,
                  },
                ]"
              >
                <div class="sidebar-image flex-none">
                  <img
                    width="24"
                    height="24"
                    :src="menu.isActive ? menu.iconActive : menu.icon"
                    alt="Icon"
                  />
                </div>
                <div
                  v-if="!isCollapsed"
                  :class="[
                    'sidebar-text flex-1 line-clamp-1',
                    { 'text-[#64748b]': menu.isActive },
                  ]"
                  :title="menu.name"
                >
                  {{ menu.name }}
                </div>
                <img
                  v-if="!isCollapsed"
                  width="16"
                  height="16"
                  :src="
                    menu.isActive
                      ? '/images/icons/ic-chevron-up-area-fill.svg'
                      : '/images/icons/ic-chevron-down-area-fill.svg'
                  "
                  alt="Icon"
                />
              </div>
              <div
                v-if="menu.isShowSubMenu && menu.isActive && !isCollapsed"
                class="sidebar-submenu"
              >
                <div
                  v-for="(sub, index2) in menu.subMenu"
                  :key="`${index}-${index2}`"
                  :class="[
                    'button-box-submenu py-2 pl-12',
                    {
                      'is-selected':
                        sub.activeNameList.indexOf(
                          $router.currentRoute.value.name
                        ) >= 0,
                    },
                  ]"
                  @click="sub.path && $router.push(sub.path)"
                >
                  <div :class="['sidebar-button', {}]">
                    <div class="sidebar-text flex-1">{{ sub.name }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Toggle Button inside sidebar at bottom-right -->
    <div class="sidebar-footer" :class="{ 'w-full': !isCollapsed }">
      <button
        type="button"
        class="sidebar-toggle workflow-back-btn"
        @click="toggleSidebar"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          alt="Toggle"
          :class="{ 'rotate-180': isCollapsed }"
        >
          <path
            d="M10.5 4.5L6 9l4.5 4.5"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="1.6"
          />
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup>
const componentName = 'SidebarManagement';
// Props types: String, Number, Boolean, Array, Object, Function, Promise
const props = defineProps({
  className: {
    type: String,
  },
  inlineStyles: {
    type: Object,
    default: function () {},
  },
  menuList: {
    type: Array,
    default: [],
  },
  isShowLogo: {
    type: Boolean,
    default: true,
  },
  logoImage: {
    type: String,
    default: '/images/logos/logo-sidebar.png',
  },
  userImage: {
    type: String,
  },
  userName: {
    type: String,
  },
  heightItem: {
    type: Number,
  },
  heightItemUnit: {
    type: String,
  },
  onClickLogo: {
    type: Function,
    default: function () {},
  },
  onClickSubMenu: {
    type: Function,
    default: function () {},
  },
});

const { $router } = useNuxtApp();
console.log('router', $router.currentRoute.value);

const isCollapsed = ref(false);

const toggleSidebar = () => {
  isCollapsed.value = !isCollapsed.value;
};
</script>

<style lang="scss">
/* Component variables */
.sidebar-management-custom {
  /* Parent style   ------------------------------ */
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 16.25rem;
  padding: 16px 14px 16px 16px;
  background-color: $color-base-white;
  height: 100vh;
  border-right: 1px solid $color-secondary-2-s-200;
  border-top-right-radius: 32px;
  border-bottom-right-radius: 32px;
  background: linear-gradient(180deg, #734eb0 2.22%, #330066 100%);
  transition: width 0.3s ease;
  position: relative;

  &.is-collapsed {
    width: 5rem;
  }

  /* Children style ------------------------------ */
  .sidebar-toggle {
    background: rgba(255, 255, 255, 0.2);
    border: none;
    border-radius: 50%;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;

    svg {
      transition: transform 0.3s ease;

      &.rotate-180 {
        transform: rotate(180deg);
      }
    }

    &:hover {
      background: rgba(255, 255, 255, 0.3);
      transform: scale(1.1);
    }
  }

  .sidebar-section {
    .is-logo {
      margin-bottom: 20px;

      img {
        width: 32px;
        height: 32px;
        // width: $size-v1sizing-8;
        // height: $size-v1sizing-8;
      }
    }

    .sidebar-lists {
      @include button-transition();
      overflow-y: auto;
      max-height: 70vh;
      min-height: 70vh;

      .button-box {
        /* height: 2.5rem; */
        height: 100%;
        border-radius: $semantic-dimension-cta-rounded-md;

        &.is-collapsed {
          .sidebar-button {
            justify-content: center;
            padding: 0.5rem;
          }
        }

        .sidebar-button {
          display: flex;
          gap: $space-x-2;
          padding: 0.5rem;
          align-items: center;
          margin: 0;
          transition: all 0.3s ease;

          &.justify-center {
            justify-content: center;
          }

          &.is-selected {
            /* background: #ffffff1a; */
            display: flex;
            align-items: center;
            justify-content: center;
            background: red;
          }

          .sidebar-image img {
            width: $semantic-dimension-icon-md;
            height: $semantic-dimension-icon-md;
          }

          .sidebar-image-sub img {
            width: $semantic-dimension-icon-sm;
            height: $semantic-dimension-icon-sm;
          }

          .rotate-img {
            transform: rotate(90deg);
          }

          .sidebar-text {
            font-family: $typography-font-family-primary;
            font-size: $semantic-text-body-sm-font-size;
            font-weight: 400;
            line-height: $semantic-text-body-sm-line-height;
            text-align: left;
            color: #d7c9ed;
          }
        }

        /* &.is-active {
          background: $specific-sidebar-navigation-background-active;
          &:hover {
            background: $specific-sidebar-navigation-background-active;
          }
        } */

        &.is-selected {
          cursor: default;
          background: #cc0099;
          border-radius: 28px;

          .sidebar-button {
            .sidebar-text {
              color: $specific-sidebar-navigation-foreground-selected;
            }
          }
        }

        &:hover {
          background: #cc0099;
          border-radius: 28px;
          .sidebar-button {
            .sidebar-text {
              color: $specific-sidebar-navigation-foreground-selected;
            }
          }
        }
      }
    }
  }

  .sidebar-submenu {
    background: #eae7f026;
    margin-top: 0.5rem;
    border-top-right-radius: 1.75rem;
    border-top-left-radius: 1.75rem;
    border-bottom-right-radius: 1.75rem;
    border-bottom-left-radius: 1.75rem;

    .button-box-submenu {
      &.is-selected {
        background: linear-gradient(
          273.84deg,
          rgba(245, 241, 254, 0) -22.26%,
          rgba(255, 255, 255, 0.18) 48.1%
        );
        border-radius: 1.75rem;

        .sidebar-button {
          .sidebar-text {
            color: $specific-sidebar-navigation-foreground-selected;
          }
        }
      }
    }
  }

  .workflow-back-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 36px;
    width: 36px;
    border-radius: 9999px;
    background: #f4f0ff;
    color: #5d45a5;
    transition: background 0.15s ease, transform 0.15s ease;
  }

  .sidebar-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 16px;
    margin-top: auto;
  }
}
</style>
